// ============================================
// LightManager — collects, culls, and uploads 2D lights to GPU
// ============================================

import type { IRenderContext } from '../core/IRenderContext';
import type { Camera2D } from '../objects/Camera2D';
import type { BufferHandle } from '../types';
import { NULL_HANDLE } from '../types';
import {
  type Light2D,
  type Light2DBase,
  type PointLight2D,
  type SpotLight2D,
  type DirectionalLight2D,
  type AmbientLight2D,
} from './Light2D';
import {
  GPU_LIGHT_STRUCT,
  LIGHT_UNIFORMS,
  GPULightType,
  GPU_LIGHT_SIZE,
} from './LightLayouts';
import { graphicsConfig } from '../GraphicsConfig';
import { resolveEmissionColor } from './kelvinToRGB';
import { IES_PROFILE_RESOLUTION } from './IESProfileManager';
import type { IESProfileManager } from './IESProfileManager';

/**
 * LightManager collects active lights, performs frustum culling against the
 * camera bounds, and writes visible lights to a GPU storage buffer + uniform
 * buffer for the light accumulation pass.
 *
 * Usage:
 * ```ts
 * const mgr = new LightManager(ctx);
 *
 * // Per-frame
 * mgr.begin(camera);
 * mgr.addLight(pointLight);
 * mgr.addLight(spotLight);
 * mgr.addLight(directionalLight);
 * mgr.setAmbient(ambientLight);
 * mgr.end(viewportWidth, viewportHeight);
 *
 * // Access GPU handles for binding
 * mgr.lightBuffer;         // storage buffer
 * mgr.uniformBuffer;       // uniform buffer
 * mgr.visiblePointLights;  // count
 * ```
 */
export class LightManager {
  private ctx: IRenderContext;

  // ---- GPU resources ----
  private _lightBuffer: BufferHandle = NULL_HANDLE;
  private _uniformBuffer: BufferHandle = NULL_HANDLE;

  // ---- CPU staging ----
  private readonly _lightWriter = GPU_LIGHT_STRUCT.createWriter();
  private readonly _uniformWriter = LIGHT_UNIFORMS.createWriter();
  private _cpuLightData: ArrayBuffer;
  private _cpuLightF32: Float32Array;

  // Pre-allocated typed arrays to avoid per-frame GC pressure
  private readonly _invVP = new Float32Array(16);
  private readonly _uniformF32: Float32Array;

  // ---- Per-frame state ----
  private _camera: Camera2D | null = null;
  private _pointCount = 0;
  private _spotCount = 0;
  private _directionalCount = 0;
  private _totalVisible = 0;
  private _volumetricLightCount = 0;
  private _ambient: AmbientLight2D | null = null;

  // ---- Type-sorted write cursors ----
  // Lights are written sorted: [point...][spot...][directional...]
  // We collect them in separate staging arrays and merge at end()
  private _pendingPoint: (PointLight2D)[] = [];
  private _pendingSpot: (SpotLight2D)[] = [];
  private _pendingDir: (DirectionalLight2D)[] = [];

  // ---- Shadow index assignment ----
  // Shadow-casting lights are assigned sequential indices (0..MAX_SHADOW_LIGHTS-1)
  // and the index is written into spotParams.y of the GPULight entry.
  // The ShadowMapPass uses these indices to know which atlas row belongs to which light.
  private _shadowLightCount = 0;
  private _shadowLightIndices: number[] = []; // maps pending light array index → shadow index or -1

  // Shadow light map: shadowLightMap[shadowIdx] = index into light buffer (for compute shader)
  private _shadowLightMap: number[] = [];

  // ---- IES Profile support ----
  private _iesProfileManager: IESProfileManager | null = null;

  // ---- Capacity ----
  private readonly _maxLights: number;

  constructor(ctx: IRenderContext, maxLights = graphicsConfig.maxLights) {
    this.ctx = ctx;
    this._maxLights = maxLights;

    // Pre-allocate CPU staging buffer for all lights
    this._cpuLightData = new ArrayBuffer(GPU_LIGHT_SIZE * maxLights);
    this._cpuLightF32 = new Float32Array(this._cpuLightData);
    this._uniformF32 = new Float32Array(LIGHT_UNIFORMS.size / 4);

    // Create GPU buffers
    this._lightBuffer = ctx.createBuffer({
      size: GPU_LIGHT_SIZE * maxLights,
      usage: ['storage', 'copy-dst'],
      label: 'light-storage-buffer',
    });

    this._uniformBuffer = ctx.createBuffer({
      size: LIGHT_UNIFORMS.size,
      usage: ['uniform', 'copy-dst'],
      label: 'light-uniform-buffer',
    });
  }

  // -----------------------------------------------------------------------
  // Accessors
  // -----------------------------------------------------------------------

  /** GPU storage buffer containing packed light data. */
  get lightBuffer(): BufferHandle { return this._lightBuffer; }

  /** GPU uniform buffer containing light uniforms (ambient, counts, screen size). */
  get uniformBuffer(): BufferHandle { return this._uniformBuffer; }

  /** Number of visible point lights this frame. */
  get visiblePointLights(): number { return this._pointCount; }

  /** Number of visible spot lights this frame. */
  get visibleSpotLights(): number { return this._spotCount; }

  /** Number of visible directional lights this frame. */
  get visibleDirectionalLights(): number { return this._directionalCount; }

  /** Total number of visible lights this frame. */
  get visibleLightCount(): number { return this._totalVisible; }

  /** Number of visible lights this frame with volumetric scattering enabled (volumetricIntensity > 0). */
  get volumetricLightCount(): number { return this._volumetricLightCount; }

  /** Maximum number of lights this manager supports. */
  get maxLights(): number { return this._maxLights; }

  /** Number of shadow-casting lights this frame (with assigned shadow indices). */
  get shadowLightCount(): number { return this._shadowLightCount; }

  /**
   * Mapping from shadow index to light buffer index.
   * `shadowLightMap[shadowIdx] = lightBufferIndex`
   * Length = shadowLightCount.
   */
  get shadowLightMap(): readonly number[] { return this._shadowLightMap; }

  /**
   * Set the IES profile manager for photometric light distribution.
   * When set, lights with `iesProfile` set to a registered profile name
   * will use the profile's angular intensity distribution.
   */
  set iesProfileManager(mgr: IESProfileManager | null) {
    this._iesProfileManager = mgr;
  }

  /** Get the current IES profile manager. */
  get iesProfileManager(): IESProfileManager | null {
    return this._iesProfileManager;
  }

  /**
   * Extend the visible light count to include VFX-injected lights.
   * Must be called after `end()` and VFX light injection, before cull pass.
   * This updates the total count so the tile culler processes the VFX lights.
   */
  addVFXLightCount(count: number): void {
    this._totalVisible += count;
    // Cap at buffer capacity
    if (this._totalVisible > this._maxLights) {
      this._totalVisible = this._maxLights;
    }
  }

  // -----------------------------------------------------------------------
  // Per-Frame Update
  // -----------------------------------------------------------------------

  /**
   * Begin a new frame of light collection.
   * Resets counts; call before addLight().
   */
  begin(camera: Camera2D): void {
    this._camera = camera;
    this._pointCount = 0;
    this._spotCount = 0;
    this._directionalCount = 0;
    this._totalVisible = 0;
    this._volumetricLightCount = 0;
    this._ambient = null;
    this._pendingPoint.length = 0;
    this._pendingSpot.length = 0;
    this._pendingDir.length = 0;
    this._shadowLightCount = 0;
    this._shadowLightIndices.length = 0;
    this._shadowLightMap.length = 0;
  }

  /**
   * Add a light to this frame. Point/spot lights are frustum-culled
   * against the camera bounds. Directional lights are always included.
   */
  addLight(light: Light2D): void {
    if (light.type === 'ambient') {
      this._ambient = light;
      return;
    }

    if (!light.enabled) return;
    if (this._totalVisible >= this._maxLights) return;

    // Frustum cull positional lights
    if ((light.type === 'point' || light.type === 'spot') && this._camera) {
      if (!this.isInFrustum(light)) return;
    }

    // Collect by type — sorted write happens in end()
    this._totalVisible++;
    switch (light.type) {
      case 'point':
        this._pendingPoint.push(light);
        this._pointCount++;
        if (light.volumetricIntensity > 0) this._volumetricLightCount++;
        break;
      case 'spot':
        this._pendingSpot.push(light);
        this._spotCount++;
        if (light.volumetricIntensity > 0) this._volumetricLightCount++;
        break;
      case 'directional':
        this._pendingDir.push(light);
        this._directionalCount++;
        break;
    }
  }

  /**
   * Set the ambient light for this frame.
   */
  setAmbient(ambient: AmbientLight2D): void {
    this._ambient = ambient;
  }

  /**
   * Finalize the frame: upload light data and uniforms to GPU.
   */
  end(viewportWidth: number, viewportHeight: number): void {
    // Write lights to CPU staging buffer sorted by type: [point...][spot...][directional...]
    // This eliminates warp divergence in the shader — each typed loop has uniform control flow.
    // Also assign shadow indices to shadow-casting lights (sequential, up to maxShadowLights).
    let writeIdx = 0;
    for (const l of this._pendingPoint) {
      const shadowIdx = l.castShadows && this._shadowLightCount < graphicsConfig.maxShadowLights
        ? this._shadowLightCount++ : -1;
      if (shadowIdx >= 0) {
        this._shadowLightMap.push(writeIdx);
      }
      this.writePointLight(l, writeIdx, shadowIdx);
      writeIdx++;
    }
    for (const l of this._pendingSpot) {
      const shadowIdx = l.castShadows && this._shadowLightCount < graphicsConfig.maxShadowLights
        ? this._shadowLightCount++ : -1;
      if (shadowIdx >= 0) {
        this._shadowLightMap.push(writeIdx);
      }
      this.writeSpotLight(l, writeIdx, shadowIdx);
      writeIdx++;
    }
    for (const l of this._pendingDir) {
      this.writeDirectionalLight(l, writeIdx);
      writeIdx++;
    }

    // Upload light storage buffer (only the used portion)
    if (this._totalVisible > 0) {
      const usedFloats = (GPU_LIGHT_SIZE / 4) * this._totalVisible;
      // Use a subarray view (no allocation — shares underlying buffer)
      this.ctx.updateBuffer(
        this._lightBuffer,
        this._cpuLightF32.subarray(0, usedFloats),
      );
    }

    // Write uniform data
    const ambient = this._ambient;

    // Resolve ambient emission color (color or temperature mode)
    const ambientRGB = ambient
      ? resolveEmissionColor(
          ambient.emissionMode,
          ambient.color,
          ambient.temperature,
          ambient.tint,
        )
      : [0.1, 0.1, 0.1] as [number, number, number];

    // Get inverse view-projection matrix from camera (for UV → world reconstruction)
    const invVP = this._invVP;
    if (this._camera) {
      this._camera.getInverseViewProjection(invVP);
    } else {
      invVP.fill(0);
    }

    this._uniformWriter.setAll({
      ambientColor: [
        ambientRGB[0],
        ambientRGB[1],
        ambientRGB[2],
        ambient?.intensity ?? 1.0,
      ],
      lightCountAndFlags: [
        this._pointCount,
        this._spotCount,
        this._directionalCount,
        0, // flags — reserved
      ],
      screenSize: [viewportWidth, viewportHeight],
      invScreenSize: [1 / viewportWidth, 1 / viewportHeight],
      invViewProjection: invVP,
      // Forward+ config (was pipeline overrides; now a live uniform read by sprite-lit.slang fs_forward_plus).
      fpConfig: [
        graphicsConfig.tileSize,
        graphicsConfig.maxLightsPerTile,
        graphicsConfig.shadowMapResolution,
        IES_PROFILE_RESOLUTION,
      ],
      pcfSamples: graphicsConfig.shadowPCFSamples,
    });

    // Copy uniform writer buffer into pre-allocated array and upload
    const src = new Uint8Array(this._uniformWriter.buffer);
    const dst = new Uint8Array(this._uniformF32.buffer);
    dst.set(src);
    this.ctx.updateBuffer(this._uniformBuffer, this._uniformF32);
  }

  // -----------------------------------------------------------------------
  // Destroy
  // -----------------------------------------------------------------------

  /** Release GPU resources. */
  destroy(): void {
    if (this._lightBuffer !== NULL_HANDLE) {
      this.ctx.destroyBuffer(this._lightBuffer);
      this._lightBuffer = NULL_HANDLE;
    }
    if (this._uniformBuffer !== NULL_HANDLE) {
      this.ctx.destroyBuffer(this._uniformBuffer);
      this._uniformBuffer = NULL_HANDLE;
    }
  }

  // -----------------------------------------------------------------------
  // Internals
  // -----------------------------------------------------------------------

  /**
   * Frustum-cull a positional light against the camera's visible bounds.
   * Returns true if the light's bounding circle intersects the camera AABB.
   */
  private isInFrustum(light: PointLight2D | SpotLight2D): boolean {
    const cam = this._camera!;
    const bounds = cam.getVisibleBounds();

    const lx = light.position[0];
    const ly = light.position[1];
    const r = light.radius;

    // Circle–AABB intersection
    const closestX = Math.max(bounds.minX, Math.min(lx, bounds.maxX));
    const closestY = Math.max(bounds.minY, Math.min(ly, bounds.maxY));
    const dx = lx - closestX;
    const dy = ly - closestY;
    return (dx * dx + dy * dy) <= (r * r);
  }

  /**
   * Write a single point light into the CPU staging buffer at the given element index.
   */
  private writePointLight(l: PointLight2D, lightIdx: number, shadowIdx: number): void {
    const rgb = resolveEmissionColor(l.emissionMode, l.color, l.temperature, l.tint);
    const iesProfileIdx = this._iesProfileManager?.getProfileIndex(l.iesProfile) ?? -1;

    this._lightWriter.setAll({
      positionAndRadius: [l.position[0], l.position[1], l.radius, l.falloff],
      colorAndIntensity: [rgb[0], rgb[1], rgb[2], l.intensity],
      directionAndType: [0, 0, 0, GPULightType.POINT],
      spotParams: [0, shadowIdx, l.shadowSoftness, l.shadowBias],
      iesParams: [l.iesIntensity, (l.iesRotation * Math.PI) / 180, iesProfileIdx, 0],
      volumetricParams: [l.volumetricIntensity, l.volumetricScattering, 0, 0],
    });

    this._copyLightToBuffer(lightIdx);
  }

  private writeSpotLight(l: SpotLight2D, lightIdx: number, shadowIdx: number): void {
    const rgb = resolveEmissionColor(l.emissionMode, l.color, l.temperature, l.tint);
    const iesProfileIdx = this._iesProfileManager?.getProfileIndex(l.iesProfile) ?? -1;

    // Pre-normalize direction
    const dx = l.direction[0];
    const dy = l.direction[1];
    const dLen = Math.sqrt(dx * dx + dy * dy) || 1;

    this._lightWriter.setAll({
      positionAndRadius: [l.position[0], l.position[1], l.radius, l.falloff],
      colorAndIntensity: [rgb[0], rgb[1], rgb[2], l.intensity],
      directionAndType: [dx / dLen, dy / dLen, Math.cos(l.innerAngle), GPULightType.SPOT],
      spotParams: [Math.cos(l.outerAngle), shadowIdx, l.shadowSoftness, l.shadowBias],
      iesParams: [l.iesIntensity, (l.iesRotation * Math.PI) / 180, iesProfileIdx, 0],
      volumetricParams: [l.volumetricIntensity, l.volumetricScattering, 0, 0],
    });

    this._copyLightToBuffer(lightIdx);
  }

  private writeDirectionalLight(l: DirectionalLight2D, lightIdx: number): void {
    const rgb = resolveEmissionColor(l.emissionMode, l.color, l.temperature, l.tint);

    // Pre-normalize direction
    const dx = l.direction[0];
    const dy = l.direction[1];
    const dLen = Math.sqrt(dx * dx + dy * dy) || 1;

    this._lightWriter.setAll({
      positionAndRadius: [0, 0, 0, 0],
      colorAndIntensity: [rgb[0], rgb[1], rgb[2], l.intensity],
      directionAndType: [dx / dLen, dy / dLen, 0, GPULightType.DIRECTIONAL],
      spotParams: [0, 0, 0, 0],
      iesParams: [0, 0, -1, 0],
      volumetricParams: [0, 0, 0, 0],
    });

    this._copyLightToBuffer(lightIdx);
  }

  /**
   * Copy the light writer's buffer to the staging buffer at the specified light index.
   */
  private _copyLightToBuffer(lightIdx: number): void {
    const floatsPerLight = GPU_LIGHT_SIZE / 4;
    const off = lightIdx * floatsPerLight;
    this._cpuLightF32.set(this._lightWriter.float32, off);
  }
}
